import { Chapter, DataNode, Report } from "graphql/types"
import { v4 } from "uuid"
import { updateReportNumbering } from "./updateReportNumbering"

/**
 * Finds a chapter by its ID in the report structure
 */
const findChapterById = (chapters: Chapter[], chapterId: string): Chapter | null => {
  for (const chapter of chapters) {
    if (chapter.id === chapterId) {
      return chapter
    }
    if (chapter.subChapter && chapter.subChapter.length > 0) {
      const found = findChapterById(chapter.subChapter, chapterId)
      if (found) return found
    }
  }
  return null
}

/**
 * Updates a chapter in the report structure
 */
const updateChapter = (chapters: Chapter[], newChapter: Chapter): Chapter[] => {
  return chapters.map((chapter) => {
    if (chapter.id === newChapter.id) {
      return newChapter
    }

    if (chapter.subChapter && chapter.subChapter.length > 0) {
      return {
        ...chapter,
        subChapter: updateChapter(chapter.subChapter, newChapter),
      }
    }

    return chapter
  })
}

/**
 * Adds a DataNode to a specific chapter in the report
 */
export const addDataNodeToChapter = (
  report: Report,
  chapterId: string,
  dataNode: DataNode
): Report => {
  const chapter = findChapterById(report.chapter, chapterId)

  if (!chapter) {
    console.warn(`Chapter with ID ${chapterId} not found`)
    return report
  }

  // Ensure the dataNode has a unique ID
  const nodeWithId = {
    ...dataNode,
    id: dataNode.id || v4(),
  }

  // Add the dataNode to the chapter
  const updatedChapter: Chapter = {
    ...chapter,
    data: [...chapter.data, nodeWithId],
  }

  // Update the report with the modified chapter
  const updatedReport: Report = updateReportNumbering({
    ...report,
    chapter: updateChapter(report.chapter, updatedChapter),
  })

  return updatedReport
}

/**
 * Global function to add DataNode to currently selected chapter
 * This function can be called from anywhere in the application
 */
let globalReportUpdateFunction: ((updatedReport: Report) => void) | null = null
let globalCurrentReport: Report | null = null
let globalCurrentChapterId: string | null = null

export const setGlobalReportContext = (
  report: Report,
  chapterId: string | null,
  updateFunction: (updatedReport: Report) => void
) => {
  globalCurrentReport = report
  globalCurrentChapterId = chapterId
  globalReportUpdateFunction = updateFunction
}

export const addToCurrentChapter = (dataNode: DataNode): boolean => {
  if (!globalCurrentReport || !globalCurrentChapterId || !globalReportUpdateFunction) {
    console.warn("No active report context found. Cannot add DataNode to chapter.")
    return false
  }

  try {
    const updatedReport = addDataNodeToChapter(
      globalCurrentReport,
      globalCurrentChapterId,
      dataNode
    )
    globalReportUpdateFunction(updatedReport)
    return true
  } catch (error) {
    console.error("Error adding DataNode to chapter:", error)
    return false
  }
}
